import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGuestContext } from '../context/GuestIdentityContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Bug, Send, Loader2, Mail } from 'lucide-react';
import TypePrioritySelectors from './feedback/TypePrioritySelectors';
import ScreenshotUploader from './feedback/ScreenshotUploader';
import { MAX_FILE_SIZE_MB, MAX_FILES } from './feedback/feedbackConstants';
import { useBottomRightPanelOpen } from '../hooks/useBottomRightPanelOpen';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating feedback button for beta testers AND guests.
 * Captures current URL automatically.
 */
export function BetaFeedbackButton() {
  const { user } = useAuth();
  const { guestId } = useGuestContext();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [screenshots, setScreenshots] = useState([]);

  const searchParams = new URLSearchParams(location.search);
  const referringPage = searchParams.get('from');

  const [form, setForm] = useState({
    type: 'bug',
    priority: 'normal',
    title: '',
    description: '',
    url: '',
    club_id: '',
    guest_email: '',
  });

  const isGuest = !user;

  // Auto-hide the FAB whenever another bottom-right widget (Emergent preview
  // chat, cookie CMP dialog, etc.) is expanded — so we don't stack on top of it.
  const buttonRef = useRef(null);
  const ignoreEls = buttonRef.current ? [buttonRef.current] : [];
  const otherPanelOpen = useBottomRightPanelOpen(200, ignoreEls);

  useEffect(() => {
    if (isOpen) {
      const pageUrl = referringPage
        ? `${window.location.origin}${referringPage}`
        : window.location.href;
      setForm((prev) => ({ ...prev, url: pageUrl }));
    }
  }, [isOpen, location, referringPage]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Pass guest_id so unauthenticated uploads can be tracked/rate-limited.
    const qs = !user && guestId ? `?guest_id=${encodeURIComponent(guestId)}` : '';
    const res = await fetch(`${API}/api/uploads/screenshot${qs}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Upload failed');
    }
    return await res.json();
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          if (screenshots.length >= MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} screenshots allowed`);
            return;
          }
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            toast.error(
              `Screenshot must be less than ${MAX_FILE_SIZE_MB}MB. Your file: ${fileSizeMB}MB`
            );
            return;
          }
          const reader = new FileReader();
          reader.onloadend = () => {
            setScreenshots((prev) => [
              ...prev,
              { file, preview: reader.result, uploadedUrl: null, uploading: false },
            ]);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  };

  const resetForm = () => {
    setForm({
      type: 'bug',
      priority: 'normal',
      title: '',
      description: '',
      url: window.location.href,
      club_id: '',
      guest_email: '',
    });
    setScreenshots([]);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Please fill in the title');
      return;
    }

    setSubmitting(true);
    try {
      const uploadedUrls = [];
      if (screenshots.length > 0) {
        setUploading(true);
        for (const ss of screenshots) {
          if (ss.uploadedUrl) {
            uploadedUrls.push(ss.uploadedUrl);
          } else {
            try {
              const result = await uploadFile(ss.file);
              uploadedUrls.push(`${API}${result.url}`);
            } catch (err) {
              toast.error(`Failed to upload screenshot: ${err.message}`);
              setSubmitting(false);
              setUploading(false);
              return;
            }
          }
        }
        setUploading(false);
      }

      const payload = {
        type: form.type,
        priority: form.priority,
        title: form.title,
        description: form.description || '',
        url: form.url,
        club_id: form.club_id || null,
        screenshot_url: uploadedUrls.length > 0 ? uploadedUrls[0] : null,
      };

      if (isGuest) {
        payload.guest_email = form.guest_email;
        payload.guest_id = guestId;
      }

      if (uploadedUrls.length > 1) {
        payload.description += `\n\n**Additional Screenshots:**\n${uploadedUrls
          .slice(1)
          .map((url, i) => `- Screenshot ${i + 2}: ${url}`)
          .join('\n')}`;
      }

      const res = await fetch(`${API}/api/beta-feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success('Feedback submitted! Thank you for helping improve the platform.');
        setIsOpen(false);
        resetForm();
      } else {
        let errorMessage = 'Failed to submit feedback';
        try {
          const err = await res.json();
          errorMessage = err.detail || errorMessage;
        } catch {
          errorMessage = `Server error (${res.status})`;
        }
        if (res.status === 401 && !isGuest) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (res.status === 400 && isGuest) {
          errorMessage = 'Please provide your email address.';
        }
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Feedback submission error:', err);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-5 z-50 flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full shadow-lg transition-all hover:scale-105 ${
          otherPanelOpen && !isOpen ? 'opacity-0 pointer-events-none scale-95' : 'opacity-100'
        }`}
        aria-hidden={otherPanelOpen && !isOpen}
        data-testid="beta-feedback-button"
      >
        <Bug className="h-4 w-4" />
        <span className="text-sm font-medium">Feedback</span>
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-amber-500" />
              Submit Feedback
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <TypePrioritySelectors form={form} setForm={setForm} />

            {isGuest && (
              <div>
                <Label htmlFor="guest_email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-500" />
                  Your Email *
                </Label>
                <Input
                  id="guest_email"
                  type="email"
                  placeholder="your@email.com (so we can follow up)"
                  value={form.guest_email}
                  onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  data-testid="feedback-guest-email"
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">
                  We&apos;ll only use this to respond to your feedback
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder={
                  form.type === 'bug'
                    ? "What's broken?"
                    : form.type === 'feature'
                    ? 'What would you like?'
                    : 'Your suggestion...'
                }
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="feedback-title"
              />
            </div>

            <div>
              <Label htmlFor="description">
                Description <span className="text-gray-400 text-xs">(optional)</span>
              </Label>
              <Textarea
                id="description"
                placeholder={
                  form.type === 'bug'
                    ? 'Steps to reproduce, what happened vs what you expected...'
                    : 'Describe your idea in detail...'
                }
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                onPaste={handlePaste}
                rows={3}
                data-testid="feedback-description"
              />
              <p className="text-xs text-gray-400 mt-1">
                Tip: You can paste a screenshot here (Ctrl/Cmd+V)
              </p>
            </div>

            <ScreenshotUploader screenshots={screenshots} setScreenshots={setScreenshots} />

            <div>
              <Label htmlFor="url">Page URL</Label>
              <Input
                id="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="text-sm text-gray-600"
                data-testid="feedback-url"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-captured from current page</p>
            </div>

            {user?.club_id && (
              <div>
                <Label>Scope</Label>
                <Select
                  value={form.club_id || 'all'}
                  onValueChange={(val) =>
                    setForm({ ...form, club_id: val === 'all' ? '' : val })
                  }
                >
                  <SelectTrigger data-testid="feedback-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Platform-wide (visible to all beta users)
                    </SelectItem>
                    <SelectItem value={user.club_id}>
                      Club-specific (my club only)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || uploading || (isGuest && !form.guest_email)}
              data-testid="feedback-submit"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Feedback
                  <Send className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BetaFeedbackButton;
